import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Modern LazyLoad component using Intersection Observer API
 * Replaces react-lazyload which uses deprecated findDOMNode (incompatible with React 19)
 */
const LazyLoad = ({ 
  children, 
  offset = 100, 
  unmountIfInvisible = false,
  className = '',
  threshold = 0.01,
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasBeenVisible(true);
          // Disconnect observer after element becomes visible
          observer.unobserve(elementRef.current);
        } else if (unmountIfInvisible) {
          setIsVisible(false);
        }
      },
      {
        rootMargin: `${offset}px`,
        threshold: threshold,
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [offset, unmountIfInvisible, threshold]);

  // If unmountIfInvisible is true, only render when visible
  // Otherwise, render once it's been visible (or immediately if already visible)
  const shouldRender = unmountIfInvisible ? isVisible : hasBeenVisible;

  return (
    <div ref={elementRef} className={className} {...props}>
      {shouldRender ? children : null}
    </div>
  );
};

LazyLoad.propTypes = {
  children: PropTypes.node.isRequired,
  offset: PropTypes.number,
  unmountIfInvisible: PropTypes.bool,
  className: PropTypes.string,
  threshold: PropTypes.number,
};

export default LazyLoad;
